import React, { useCallback } from 'react';
import _ from 'lodash';
import cx from 'classnames';
import PropTypes from 'prop-types';
import htmlParser from 'html-react-parser';
import sanitizeHtml from 'sanitize-html';
import { HighlightPhraseFilter } from '@xengage/gw-portals-util-js';
import { ImageComponent } from 'gw-components-platform-react';
import styles from './FaqItemComponent.module.scss';
import { AccordionCard } from '@jutro/legacy/components';

function FaqItemComponent(props) {
    const { value, searchText } = props;

    const readValue = useCallback(
        (id, compPath) => {
            return _.get(value, compPath);
        },
        [value]
    );

    const renderAccordionHeader = useCallback(
        (isOpen) => {
            const plainTitleText = readValue('accordionHeader', 'question');
            const highlightedText = HighlightPhraseFilter.highlightText(
                plainTitleText,
                searchText,
                styles.gwHighlightedText
            );

            const titleID = readValue('accordionHeader', 'id');
            const parsedHTML = htmlParser(highlightedText);

            return (
                <React.Fragment>
                    <h3 id={titleID}
                        className={styles['gw-faq-topic-title']}>
                        <div className={
                            cx(styles['header-caret'], 'icon-accordion-down', isOpen ? 'icon-rotate-180 padding-left-20' : 'padding-right-20')}
                        />
                        
                        <span className={styles['ml-topic-question']}>{parsedHTML}</span>
                    </h3>
                </React.Fragment>
            );
        },
        [readValue, searchText]
    );

    const renderLinkComponent = useCallback(() => {
        const url = _.get(value, 'link.url');
        let text = readValue('linkText', 'link.name');
        if (!_.isUndefined(text)) {
            text = HighlightPhraseFilter.highlightText(text, searchText, styles.gwHighlightedText);
            text = htmlParser(text);
        }

        if (url === undefined) {
            return null;
        }

        return (
            <div className={styles['gw-faq-topic-section']}>
                <a
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.gwFaqItemLink}
                >
                    {text === undefined ? url : text}
                </a>
            </div>
        );
    }, [readValue, value, searchText]);

    const renderVideoComponent = useCallback(() => {
        const videoId = _.get(value, 'videoId');
        const youtubeUrl = `https://www.youtube.com/embed/${videoId}`;

        if (videoId === undefined) {
            return null;
        }

        return (
            <div className={styles['gw-faq-topic-section']}>
                <iframe
                    title={_.get(value, 'id')}
                    className={styles.gwFaqYouTube}
                    frameBorder="0"
                    allowFullScreen
                    width="100%"
                    height="100%"
                    src={youtubeUrl}
                />
            </div>
        );
    }, [value]);

    const renderTextComponent = useCallback(() => {
        const allowedTags = ['img', 'ul', 'li', 'h2', 'a', 'strong', 'br', 'b'];
        const escapeSymbols = {
            '<': '&lt;',
            '>': '&gt;'
        };

        const answer = readValue('answerText', 'long');
        const sanitizedAnswer = sanitizeHtml(answer, {
            allowedTags,
            textFilter: (text) => {
                return text.replace(/<|>/gi, (matched) => escapeSymbols[matched]);
            }
        });
        const highlightedText = HighlightPhraseFilter.highlightText(
            sanitizedAnswer,
            searchText,
            styles.gwHighlightedText
        );

        const parsedHTML = htmlParser(highlightedText, {
            replace: (component) => {
                if (component.name === 'img') {
                    return <ImageComponent {...component.attribs} />;
                }
                return component;
            }
        });

        return (
            <div className={styles['gw-faq-topic-section']}>{parsedHTML}</div>
        );
    }, [readValue, searchText]);

    return (
        <li>
            <div className={styles.gwFaqItemContainer}>
                <AccordionCard
                    id={`accordion_${_.get(value, 'id')}`}
                    renderHeader={renderAccordionHeader}
                    cardTitleClassName="titleContainer"
                >
                    {renderTextComponent()}
                    {renderLinkComponent()}
                    {renderVideoComponent()}
                </AccordionCard>
            </div>
        </li>
    );
}

FaqItemComponent.propTypes = {
    value: PropTypes.shape({}).isRequired,
    searchText: PropTypes.string.isRequired
};
export default FaqItemComponent;
